// src/app/pages/legislative-tracker/legislative-tracker.ts

import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { SeoService } from '../../services/seo.service';
import { CommonModule, DatePipe, UpperCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { CongressService, Bill } from '../../services/congress.service';

interface Topic {
  key: string;
  label: string;
}

// Hardcoded featured bills for the SAVE Act lineage and DHS shutdown - always shown correctly
// regardless of Congress API response. Keep these updated manually as status changes.
export const SAVE_ACT_BILLS: Bill[] = [
  {
    billType: 's',
    billNumber: '1383',
    title: 'SAVE America Act - Safeguard American Voter Eligibility Act (Active / Currently Tracked)',
    policyArea: 'Voting Rights',
    status: 'Senate - Stalled, In Recess',
    statusColor: 'status-stalled',
    lydiaPosition: 'opposes',
    introducedDate: '2025-01-01',
    latestAction: {
      actionDate: '2026-03-26',
      text: 'Multiple cloture attempts have now failed. Mar 21: cloture failed 41–49. Mar 24: two motions to table passed 53–47. Mar 26: cloture on Husted photo-ID amendment failed 53–47; cloture on the H.R. 7147 vehicle also failed 53–47. Senate adjourned into recess until April 13. VP Vance publicly acknowledged they lack 50 votes to break the filibuster. Bill remains active but effectively stalled. Requires documentary proof of citizenship to register; mandates state voter roll submission to DHS SAVE database. Lydia opposes this bill as a voter suppression measure targeting low-income and minority voters.'
    },
    url: 'https://www.congress.gov/bill/119th-congress/senate-bill/1383',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '8800',
    title: 'National Defense Authorization Act, FY2027 — Carries SAVE Act Voter-ID Rider',
    policyArea: 'National Security',
    status: 'House Passed — Pending Senate',
    statusColor: 'status-passed',
    lydiaPosition: 'opposes',
    introducedDate: '2026-05-13',
    latestAction: {
      actionDate: '2026-07-22',
      text: 'House passed the FY2027 defense authorization bill 216–212 on July 22, with House Republicans attaching SAVE America Act voter-ID and citizenship-proof provisions as a rider — one of several vehicles used this month to try to force the Senate\'s hand after S. 1383 stalled in March. Sent to the Senate; outcome pending. Lydia opposes both the SAVE Act rider and the underlying $1.15 trillion defense authorization itself.'
    },
    url: 'https://www.congress.gov/bill/119th-congress/house-bill/8800',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '7008',
    title: 'Stop Insider Trading Act — Carries SAVE Act Photo-ID Rider',
    policyArea: 'Congress',
    status: 'House Passed — Pending Senate',
    statusColor: 'status-passed',
    lydiaPosition: 'opposes',
    introducedDate: '2026-01-12',
    latestAction: {
      actionDate: '2026-07-23',
      text: 'House passed this ban on members of Congress trading stocks 232–198 on July 22, with a SAVE Act-style photo-ID voting requirement attached as a rider. Received in the Senate July 23; outcome pending. Lydia\'s opposition is to the SAVE Act rider specifically — not a statement on the underlying stock-trading ban.'
    },
    url: 'https://www.congress.gov/bill/119th-congress/house-bill/7008',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '8595',
    title: 'FY2027 State Department & National Security Appropriations — Carries SAVE Act Rider',
    policyArea: 'International Affairs',
    status: 'House Passed — Pending Senate',
    statusColor: 'status-passed',
    lydiaPosition: 'opposes',
    introducedDate: '2026-04-30',
    latestAction: {
      actionDate: '2026-07-29',
      text: 'House passed this FY2027 State Department and national security funding bill on July 15, reported to carry SAVE America Act provisions among the vehicles House Republicans used this month to pressure the Senate. Received in the Senate July 29; outcome pending. Lydia\'s opposition is to the SAVE Act rider specifically, not the underlying appropriations.'
    },
    url: 'https://www.congress.gov/bill/119th-congress/house-bill/8595',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '7147',
    title: 'DHS Appropriations Act, 2026 — Signed into Law (Public Law 119-86)',
    policyArea: 'Appropriations',
    status: 'Signed into Law',
    statusColor: 'status-law',
    lydiaPosition: 'supports',
    introducedDate: '2026-01-20',
    latestAction: {
      actionDate: '2026-04-30',
      text: 'After the Senate tabled the House\'s competing amendment on April 2, the House gave in on April 30 — suspending the rules, receding from its own amendment, and voice-voting to concur in the Senate\'s version, which funds TSA, FEMA, Coast Guard, and CISA while excluding ICE and CBP. Presented to and signed by the President the same day, becoming Public Law No. 119-86 and ending the partial DHS shutdown.'
    },
    url: 'https://www.congress.gov/bill/119th-congress/house-bill/7147',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '22',
    title: 'SAVE Act - Safeguard American Voter Eligibility Act (119th Congress, House-passed, Senate stalled)',
    policyArea: 'Voting Rights',
    status: 'Senate Stalled',
    statusColor: 'status-stalled',
    lydiaPosition: 'opposes',
    introducedDate: '2025-01-03',
    latestAction: {
      actionDate: '2025-04-10',
      text: 'Passed House 220–208 in April 2025. Failed to reach 60-vote Senate threshold. Superseded by SAVE America Act (S. 1383).'
    },
    url: 'https://www.congress.gov/bill/119th-congress/house-bill/22',
    congress: 119
  },
  {
    billType: 'hr',
    billNumber: '8281',
    title: 'SAVE Act - Safeguard American Voter Eligibility Act (Original 118th Congress version)',
    policyArea: 'Voting Rights',
    status: 'Died in Senate',
    statusColor: 'status-vetoed',
    lydiaPosition: 'opposes',
    introducedDate: '2024-05-07',
    latestAction: {
      actionDate: '2024-12-01',
      text: 'Original version introduced by Rep. Chip Roy (TX). Passed House, never received Senate vote. 118th Congress ended; bill died. Predecessor to H.R. 22 and S. 1383.'
    },
    url: 'https://www.congress.gov/bill/118th-congress/house-bill/8281',
    congress: 119
  }
];

@Component({
  selector: 'app-legislative-tracker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DatePipe,
    UpperCasePipe
  ],
  templateUrl: './legislative-tracker.html',
  styleUrls: ['./legislative-tracker.css']
})
export class LegislativeTrackerComponent implements OnInit {

  bills: Bill[] = [];
  filteredBills: Bill[] = [];
  isLoading = false;
  searchQuery = '';
  activeTopic = 'digital';
  errorMessage = '';
  michiganOnly = false;
  showBookmarks = false;
  bookmarks = new Set<string>();
  shareToastVisible = false;

  // SAVE Act pinned watch section
  saveActBills = SAVE_ACT_BILLS;
  showSaveActWatch = true;

  topics: Topic[] = [
    { key: 'michigan', label: '🦅 Michigan Bills' },
    { key: 'voting', label: '🗳 Voting Rights' },
    { key: 'digital', label: 'Digital Rights' },
    { key: 'environment', label: 'Environment' },
    { key: 'healthcare', label: 'Healthcare' },
    { key: 'labor', label: 'Labor & Workers' },
    { key: 'civil', label: 'Civil Liberties' },
    { key: 'economy', label: 'Economy' },
    { key: 'ai', label: 'AI & Tech' },
    { key: 'social', label: 'Social Security' },
  ];

  // Michigan delegation bioguide IDs (for future Michigan filter via API)
  private michiganIds = [
    'P000595','S001208','B001301','M001194','S001221',
    'H001058','W000798','D000624','B001321','M001237',
    'M001136','J000307','S001215','T000481','T000488'
  ];

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly trackerPageId =
    `${this.baseUrl}/tracker#tracker`;

  private readonly billListId =
    `${this.baseUrl}/tracker#bill-list`;

  constructor(
    private congressService: CongressService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    this.seo.update(
      'Legislative Tracker',
      'Track how Michigan senators vote on key legislation in real time.',
      'tracker'
    );

    this.seo.setStructuredData({
      "@context": "https://schema.org",

      "@graph": [
        {
          "@type": "CollectionPage",

          "@id": this.trackerPageId,

          "url": `${this.baseUrl}/tracker`,

          "name": "Lydia Christensen Legislative Tracker",

          "description":
            "Track how Michigan senators vote on key legislation in real time.",

          "isPartOf": {
            "@id": this.seo.websiteId
          },

          "about": {
            "@id": this.seo.lydiaId
          },

          "publisher": {
            "@id": this.seo.campaignId
          },

          "mainEntity": {
            "@id": this.billListId
          },

          "inLanguage": "en-US"
        },

        {
          "@type": "ItemList",

          "@id": this.billListId,

          "name": "Tracked Legislation",

          "description":
            "Legislation tracked by the Lydia Christensen campaign.",

          "itemListOrder":
            "https://schema.org/ItemListOrderAscending"
        }
      ]
    });

    this.loadBookmarks();

    // Check URL for deep-linked bill
    this.route.queryParams.subscribe(params => {
      if (params['bill']) {
        this.searchQuery = params['bill'];
        this.onSearch();
      } else {
        this.loadBills('digital');
      }
    });
  }

  setTopic(key: string): void {
    this.activeTopic = key;
    this.showBookmarks = false;

    // Auto-expand the SAVE Act watch panel when voting rights tab is selected
    if (key === 'voting') {
      this.showSaveActWatch = true;
    }

    this.loadBills(key);
  }

  toggleSaveActWatch(): void {
    this.showSaveActWatch = !this.showSaveActWatch;
  }

  onSearch(): void {
    if (!this.searchQuery.trim()) {
      return;
    }

    this.activeTopic = '';
    this.showBookmarks = false;
    this.loadBills(null, this.searchQuery.trim());
  }

  toggleBookmarks(): void {
    this.showBookmarks = !this.showBookmarks;
    this.applyFilters();
  }

  toggleBookmark(bill: Bill): void {
    const key =
      `${bill.billType}-${bill.billNumber}`;

    if (this.bookmarks.has(key)) {
      this.bookmarks.delete(key);
    } else {
      this.bookmarks.add(key);
    }

    this.saveBookmarks();
    this.applyFilters();
    this.cdr.detectChanges();
  }

  shareBill(bill: Bill): void {
    const url =
      `${window.location.origin}${window.location.pathname}#/tracker?bill=${bill.billType.toUpperCase()}-${bill.billNumber}`;

    navigator.clipboard.writeText(url).then(() => {
      this.shareToastVisible = true;
      this.cdr.detectChanges();

      setTimeout(() => {
        this.shareToastVisible = false;
        this.cdr.detectChanges();
      }, 2500);
    });
  }

  private loadBills(
    topicKey: string | null,
    searchQuery?: string
  ): void {

    this.isLoading = true;
    this.bills = [];
    this.errorMessage = '';

    const obs = topicKey
      ? this.congressService.getBillsByTopic(
          topicKey,
          24
        )
      : this.congressService.searchBills(
          searchQuery ?? '',
          24
        );

    obs.subscribe({

      next: (bills) => {
        this.bills = bills;
        this.isLoading = false;
        this.applyFilters();
        this.cdr.detectChanges();
      },

      error: (err) => {
        console.error(
          'Bill load error:',
          err
        );

        this.errorMessage =
          'Could not load bills. Please try again.';

        this.isLoading = false;
        this.cdr.detectChanges();
      },

      complete: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }

    });
  }

  private applyFilters(): void {

    let result = [...this.bills];

    if (this.showBookmarks) {
      result = result.filter(
        bill =>
          this.bookmarks.has(
            `${bill.billType}-${bill.billNumber}`
          )
      );
    }

    this.filteredBills = result;
  }

  private loadBookmarks(): void {

    try {

      const saved =
        localStorage.getItem(
          'tracker_bookmarks'
        );

      if (saved) {
        this.bookmarks =
          new Set(
            JSON.parse(saved)
          );
      }

    } catch {}
  }

  private saveBookmarks(): void {

    try {

      localStorage.setItem(
        'tracker_bookmarks',
        JSON.stringify(
          [...this.bookmarks]
        )
      );

    } catch {}
  }
}